import React, { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { RotatingLines } from 'react-loader-spinner';

import { Input } from '../common-inputs';
import SelectedOption from './SelectedOption';
import MultiselectMenu from './MultiselectMenu';
import useBodyClicker from '../../hooks/useBodyClicker';
import useInitialRender from '../../hooks/useInitialRender';

import './MultiselectDropdown.scss';

const MultiselectDropdown = ({
  options = [],
  value = [],
  onChange,
  onSearch,
  isInitialSearch = false,
  name,
  onBlur,
  onFocus,
  isError,
  isDebounce = false,
  debounceTimeout = 300,
  disabled,
  isReadOnly,
  isTop,
  onAddNewOption,
  placeholder,
  disableNewOptions,
  maxHeight,
  disableSelectedOptionsRender,
  isLoading,
  usePortal = false,
}) => {
  const { t } = useTranslation();

  const [searchStr, setSearchStr] = useState('');
  const [localOptions, setLocalOptions] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState(null);

  const wrapperRef = useRef(null);
  const portalRef = useRef(null);

  const handleCloseDropdown = useCallback(() => {
    setDropdownOpen(false);
    setSearchStr('');
  }, []);

  const dropdownRef = useBodyClicker(handleCloseDropdown);
  const isInitialRender = useInitialRender();

  const openDropdown = useCallback(() => {
    !disabled ? setDropdownOpen(true) : null;
  }, [disabled]);

  const handleOptionSelect = useCallback(
    (option) => {
      if (!disabled && !value.some((selectedOption) => selectedOption.value === option.value)) {
        onChange([...value, option]);
        handleCloseDropdown();
      }
    },
    [value, onChange, handleCloseDropdown, disabled],
  );

  const handleRemoveOption = useCallback(
    (option) => {
      if (!disabled) {
        const updatedOptions = value.filter((selectedOption) => selectedOption.value !== option.value);
        onChange(updatedOptions);
      }
    },
    [onChange, value, disabled],
  );

  const handleFilterChange = useCallback((value) => {
    setSearchStr(value);
  }, []);

  const filteredOptions = useMemo(
    () =>
      searchStr
        ? [
            ...(localOptions.some(({ label }) => label.toLowerCase() === searchStr.toLowerCase()) || disableNewOptions
              ? []
              : [{ value: 'NEW_VALUE', label: `${t('add')} "${searchStr}"` }]),
            ...localOptions.filter(({ label }) => label.toLowerCase().includes(searchStr.toLowerCase())),
          ]
        : localOptions,
    [searchStr, localOptions, t, disableNewOptions],
  );

  const handleAddNewOption = useCallback(() => {
    setLocalOptions((prevOptions) => [...prevOptions, { value: searchStr, label: searchStr }]);
    onChange([...value, { value: searchStr, label: searchStr }]);

    if (onAddNewOption) {
      onAddNewOption({ value: searchStr, label: searchStr });
    }

    handleCloseDropdown();
  }, [searchStr, onChange, value, handleCloseDropdown, onAddNewOption]);

  useEffect(() => {
    setLocalOptions(options);
  }, [options]);

  useEffect(() => {
    if (onSearch && !isInitialRender) {
      onSearch?.(searchStr);
    }
  }, [searchStr]);

  useEffect(() => {
    if (isInitialSearch) {
      setSearchStr(searchStr);
    }
  }, [isInitialSearch]);

  useLayoutEffect(() => {
    if (dropdownOpen && usePortal && wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      setMenuPosition({
        top: isTop ? undefined : rect.bottom + 5,
        bottom: isTop ? window.innerHeight - rect.top + 5 : undefined,
        left: rect.left,
        width: rect.width,
      });
    } else {
      setMenuPosition(null);
    }
  }, [dropdownOpen, usePortal, isTop]);

  useEffect(() => {
    if (!usePortal || !dropdownOpen) return;
    const handleClick = (e) => {
      if (wrapperRef.current?.contains(e.target) || portalRef.current?.contains(e.target)) {
        return;
      }
      handleCloseDropdown();
    };
    document.body.addEventListener('click', handleClick);
    return () => document.body.removeEventListener('click', handleClick);
  }, [usePortal, dropdownOpen, handleCloseDropdown]);

  const menuContent = dropdownOpen && (
    <MultiselectMenu
      options={filteredOptions}
      onSelect={handleOptionSelect}
      searchStr={searchStr}
      onAddNewOption={handleAddNewOption}
      isTop={isTop}
      selectedValues={value}
    />
  );

  return (
    <div
      className={clsx('multiselect_wrapper', isError && 'multiselect_wrapper--error')}
      style={{ maxHeight }}
      ref={usePortal ? wrapperRef : dropdownRef}>
      {!disableSelectedOptionsRender
        ? value.map((option) => <SelectedOption key={option.value} option={option} onRemove={handleRemoveOption} />)
        : null}
      <Input
        onClick={openDropdown}
        size='sm'
        placeholder={placeholder || t('search')}
        value={searchStr}
        extraWrapperClass={'multiselect_input_wrapper'}
        extraInputClass={'multiselect_input_field'}
        onChange={handleFilterChange}
        name={name}
        onBlur={onBlur}
        onFocus={onFocus}
        isError={isError}
        disabled={disabled}
        isReadOnly={isReadOnly}
        debounce={isDebounce}
        timeout={debounceTimeout}
        RightIcon={isLoading ? <RotatingLines strokeColor='#fdbf47' height={10} width={20} /> : null}
      />

      {usePortal && dropdownOpen && menuPosition
        ? ReactDOM.createPortal(
            <div
              ref={portalRef}
              className='multiselect_portal'
              style={{
                position: 'fixed',
                top: menuPosition.top,
                bottom: menuPosition.bottom,
                left: menuPosition.left,
                width: menuPosition.width,
                zIndex: 9999,
              }}
              onClick={(e) => e.stopPropagation()}>
              {menuContent}
            </div>,
            document.body,
          )
        : !usePortal && menuContent}
    </div>
  );
};

export default memo(MultiselectDropdown);
